import * as z from 'zod/mini';

import { WALLET_ACCOUNT_TYPES } from '@/constants/deposit';

export const railInputFieldSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: z._default(z.string(), 'string'),
  validation: z.optional(z.nullable(z.string())),
});

export type RailInputField = z.infer<typeof railInputFieldSchema>;

export const cashierApiRailSchema = z.object({
  code: z.string(),
  name: z.string(),
  inputFields: z.optional(z.array(railInputFieldSchema)),
});

export type CashierApiRail = z.infer<typeof cashierApiRailSchema>;

export const cashierApiTileSchema = z.object({
  kind: z.literal('api'),
  gatewayAccountId: z.string(),
  label: z.string(),
  logoUrl: z.nullable(z.string()),
  rails: z.optional(z.array(cashierApiRailSchema)),
});

export const cashierWalletAccountTileSchema = z.object({
  kind: z.literal('wallet'),
  accountType: z.enum(WALLET_ACCOUNT_TYPES),
  /** Destination identity for `/choose`. Not unique by `accountType` alone. */
  traderWalletId: z.string().check(z.minLength(1)),
  name: z.string(),
  /** Operator destination title; falls back to `name` on screen when absent. */
  label: z.optional(z.string()),
});

export const cashierMethodTileSchema = z.discriminatedUnion('kind', [
  cashierApiTileSchema,
  cashierWalletAccountTileSchema,
]);

export type CashierMethodTile = z.infer<typeof cashierMethodTileSchema>;
export type CashierApiTile = z.infer<typeof cashierApiTileSchema>;
export type CashierWalletAccountTile = z.infer<typeof cashierWalletAccountTileSchema>;

export function isWalletAccountTile(tile: CashierMethodTile): tile is CashierWalletAccountTile {
  return tile.kind === 'wallet';
}

/**
 * One unrecognised tile must not fail the whole catalogue. Unknown `kind`, wallet rows that are
 * not a known `accountType`, or wallet rows missing `traderWalletId` are dropped (same idea as
 * unknown `rails[].code` on API tiles). Extra keys such as `routeId` and `priority` are stripped.
 */
export function keepKnownMethodTiles(raw: readonly unknown[]): CashierMethodTile[] {
  const kept: CashierMethodTile[] = [];
  for (const item of raw) {
    const parsed = cashierMethodTileSchema.safeParse(item);
    if (parsed.success) kept.push(parsed.data);
  }
  return kept;
}

export interface CashierMethods {
  methods: CashierMethodTile[];
}

export const cashierMethodsSchema = z.pipe(
  z.object({ methods: z.array(z.unknown()) }),
  z.transform((payload: { methods: unknown[] }): CashierMethods => ({
    methods: keepKnownMethodTiles(payload.methods),
  })),
);
